import { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";

async function getAuthHeaders() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return {};
  return { Authorization: `Bearer ${session.access_token}` };
}

const ROLES = ["admin", "uploader", "family", "readonly"];

const ROLE_INFO = {
  admin:    { label: "Admin",     color: "#f59e0b", desc: "Full access — view, upload, delete, manage users" },
  uploader: { label: "Uploader",  color: "#60a5fa", desc: "Can view and upload photos" },
  family:   { label: "Family",    color: "#34d399", desc: "Can view photos only" },
  readonly: { label: "View Only", color: "#94a3b8", desc: "Can view photos — expires in 10 days" },
};

export default function Admin() {
  const [activeTab, setActiveTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  // New user form
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState("family");
  const [creating, setCreating] = useState(false);

  // New invite code form
  const [newCode, setNewCode] = useState("");
  const [codeRole, setCodeRole] = useState("family");
  const [codeMaxUses, setCodeMaxUses] = useState(1);
  const [codeExpiry, setCodeExpiry] = useState("");
  const [creatingCode, setCreatingCode] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchCodes();
  }, []);

  async function fetchUsers() {
  setLoading(true);
  try {
    const headers = await getAuthHeaders();
    const res = await fetch("/api/users", { headers });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error);
    setUsers(data.users || []);  // Add || [] fallback
  } catch (err) {
    setError(err.message);
    setUsers([]);  // Make sure users is always an array
  }
  setLoading(false);
}

  async function fetchCodes() {
  try {
    const headers = await getAuthHeaders();
    const res = await fetch("/api/invite-codes", { headers });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error);
    setCodes(data.codes || []);  // Add || [] fallback
  } catch (err) {
    console.error("Failed to fetch codes:", err);
    setCodes([]);  // Make sure codes is always an array
  }
}

  async function createUser(e) {
    e.preventDefault();
    setCreating(true);
    setMessage(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch("/api/users", {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ email: newEmail, role: newRole }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setMessage({ type: "success", text: `Invite sent to ${newEmail}! They will receive an email to set their password.` });
      setNewEmail("");
      setNewRole("family");
      fetchUsers();
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    }
    setCreating(false);
  }

  async function updateRole(userId, role) {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`/api/users/${userId}/role`, {
        method: "PATCH",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ role }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, role } : u));
      setMessage({ type: "success", text: `Role updated to ${role}!` });
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    }
  }

  async function deleteUser(user) {
    if (!window.confirm(`Delete ${user.email}? This cannot be undone.`)) return;
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`/api/users/${user.id}`, { method: "DELETE", headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setUsers((prev) => prev.filter((u) => u.id !== user.id));
      setMessage({ type: "success", text: `${user.email} deleted!` });
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    }
  }

  async function createCode(e) {
    e.preventDefault();
    setCreatingCode(true);
    setMessage(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch("/api/invite-codes", {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          code: newCode,
          role: codeRole,
          max_uses: parseInt(codeMaxUses),
          expires_at: codeExpiry || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setMessage({ type: "success", text: `Invite code "${newCode.toUpperCase()}" created!` });
      setNewCode("");
      setCodeRole("family");
      setCodeMaxUses(1);
      setCodeExpiry("");
      fetchCodes();
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    }
    setCreatingCode(false);
  }

  async function deleteCode(code) {
    if (!window.confirm(`Delete code "${code.code}"?`)) return;
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`/api/invite-codes/${code.id}`, { method: "DELETE", headers });
      if (!res.ok) throw new Error("Failed to delete code");
      setCodes((prev) => prev.filter((c) => c.id !== code.id));
      setMessage({ type: "success", text: `Code "${code.code}" deleted!` });
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    }
  }

  function generateCode() {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let result = "";
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewCode(result);
  }

  function copySignupLink() {
    navigator.clipboard.writeText(`https://lupha.duckdns.org/signup`);
    setMessage({ type: "success", text: "Signup link copied to clipboard!" });
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Admin Panel</h1>
        <p className="page-subtitle">Manage users and invite codes</p>
      </div>

      {message && (
        <div className={message.type === "success" ? "message-success" : "error-banner"}>
          {message.type === "success" ? "✅" : "⚠️"} {message.text}
        </div>
      )}

      {/* Tabs */}
      <div className="admin-tabs">
        <button
          className={`admin-tab ${activeTab === "users" ? "admin-tab-active" : ""}`}
          onClick={() => setActiveTab("users")}
        >
          👥 Users ({users.length})
        </button>
        <button
          className={`admin-tab ${activeTab === "codes" ? "admin-tab-active" : ""}`}
          onClick={() => setActiveTab("codes")}
        >
          🔑 Invite Codes ({codes.length})
        </button>
      </div>

      {/* Users Tab */}
      {activeTab === "users" && (
        <>
          {/* Role Legend */}
          <div className="role-legend">
            {Object.entries(ROLE_INFO).map(([key, info]) => (
              <div key={key} className="role-legend-item">
                <span className="role-badge-sm" style={{ background: info.color }}>{info.label}</span>
                <span className="role-legend-desc">{info.desc}</span>
              </div>
            ))}
          </div>

          {/* Invite User */}
          <div className="admin-section">
            <h2 className="section-label">Invite User via Email</h2>
            <form className="create-user-form" onSubmit={createUser}>
              <div className="form-row form-row-2">
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    className="form-input"
                    type="email"
                    placeholder="family@email.com"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Role</label>
                  <select className="form-input" value={newRole} onChange={(e) => setNewRole(e.target.value)}>
                    {ROLES.map((r) => (
                      <option key={r} value={r}>{ROLE_INFO[r].label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <button className="create-user-btn" type="submit" disabled={creating}>
                {creating ? "Sending..." : "📧 Send Invite Email"}
              </button>
            </form>
          </div>

          {/* User List */}
          <div className="admin-section">
            <h2 className="section-label">Current Users ({users.length})</h2>
            {loading ? (
              <div className="page-loading"><div className="spinner" /></div>
            ) : error ? (
              <div className="error-banner">⚠️ {error}</div>
            ) : (
              <div className="user-list">
                {users.map((user) => (
                  <div key={user.id} className="user-card">
                    <div className="user-info">
                      <div className="user-email">{user.email}</div>
                      <div className="user-meta">
                        Joined {new Date(user.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                        {user.last_sign_in && ` · Last login ${new Date(user.last_sign_in).toLocaleDateString()}`}
                        {user.expires_at && (
                          <span className="user-expiry"> · Expires {new Date(user.expires_at).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>
                    <div className="user-actions">
                      <select
                        className="role-select"
                        value={user.role}
                        onChange={(e) => updateRole(user.id, e.target.value)}
                        style={{ borderColor: ROLE_INFO[user.role]?.color }}
                      >
                        {ROLES.map((r) => (
                          <option key={r} value={r}>{ROLE_INFO[r].label}</option>
                        ))}
                      </select>
                      <button className="delete-user-btn" onClick={() => deleteUser(user)}>🗑️</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Invite Codes Tab */}
      {activeTab === "codes" && (
        <>
          {/* Signup Link */}
          <div className="admin-section">
            <h2 className="section-label">Signup Link</h2>
            <p className="signup-link-desc">
              Share this link via text message along with an invite code:
            </p>
            <div className="signup-link-box">
              <code className="signup-link">https://lupha.duckdns.org/signup</code>
              <button className="copy-btn" onClick={copySignupLink}>📋 Copy</button>
            </div>
          </div>

          {/* Create Code */}
          <div className="admin-section">
            <h2 className="section-label">Create Invite Code</h2>
            <form className="create-user-form" onSubmit={createCode}>
              <div className="form-row form-row-2">
                <div className="form-group">
                  <label className="form-label">Invite Code</label>
                  <div className="code-input-row">
                    <input
                      className="form-input"
                      type="text"
                      placeholder="e.g. STALEY2026"
                      value={newCode}
                      onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                      required
                      maxLength={20}
                    />
                    <button type="button" className="generate-btn" onClick={generateCode}>
                      🎲 Generate
                    </button>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Role Assigned</label>
                  <select className="form-input" value={codeRole} onChange={(e) => setCodeRole(e.target.value)}>
                    {ROLES.map((r) => (
                      <option key={r} value={r}>{ROLE_INFO[r].label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-row form-row-2">
                <div className="form-group">
                  <label className="form-label">Max Uses</label>
                  <input
                    className="form-input"
                    type="number"
                    min={1}
                    max={100}
                    value={codeMaxUses}
                    onChange={(e) => setCodeMaxUses(e.target.value)}
                  />
                  <span className="form-hint">How many people can use this code</span>
                </div>
                <div className="form-group">
                  <label className="form-label">Expiry Date (optional)</label>
                  <input
                    className="form-input"
                    type="date"
                    value={codeExpiry}
                    onChange={(e) => setCodeExpiry(e.target.value)}
                  />
                  <span className="form-hint">Leave blank for no expiry</span>
                </div>
              </div>
              <button className="create-user-btn" type="submit" disabled={creatingCode}>
                {creatingCode ? "Creating..." : "🔑 Create Invite Code"}
              </button>
            </form>
          </div>

          {/* Code List */}
          <div className="admin-section">
            <h2 className="section-label">Active Invite Codes ({codes.length})</h2>
            {codes.length === 0 ? (
              <div className="empty-state" style={{ padding: "32px 0" }}>
                <div className="empty-icon">🔑</div>
                <p>No invite codes yet — create one above!</p>
              </div>
            ) : (
              <div className="user-list">
                {codes.map((code) => {
                  const isExpired = code.expires_at && new Date(code.expires_at) < new Date();
                  const isUsedUp = code.uses >= code.max_uses;
                  return (
                    <div key={code.id} className={`user-card ${isExpired || isUsedUp ? "code-inactive" : ""}`}>
                      <div className="user-info">
                        <div className="code-display">
                          <span className="code-text">{code.code}</span>
                          <span className="role-badge-sm" style={{ background: ROLE_INFO[code.role]?.color }}>
                            {ROLE_INFO[code.role]?.label}
                          </span>
                          {isExpired && <span className="code-status expired">Expired</span>}
                          {isUsedUp && !isExpired && <span className="code-status used">Used Up</span>}
                        </div>
                        <div className="user-meta">
                          Uses: {code.uses}/{code.max_uses}
                          {code.expires_at && ` · Expires ${new Date(code.expires_at).toLocaleDateString()}`}
                          {` · Created ${new Date(code.created_at).toLocaleDateString()}`}
                        </div>
                      </div>
                      <div className="user-actions">
                        <button
                          className="copy-code-btn"
                          onClick={() => {
                            navigator.clipboard.writeText(code.code);
                            setMessage({ type: "success", text: `Code "${code.code}" copied!` });
                          }}
                        >
                          📋 Copy
                        </button>
                        <button className="delete-user-btn" onClick={() => deleteCode(code)}>🗑️</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
